import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Row } from 'react-table';

import { useFilterBarParams } from 'src/common/components/filter-bar/use-params';
import { generateColumn } from 'src/common/components/table/LegacyTable';
import { Journey } from 'src/common/interfaces/journey.interface';
import { FixedLoadingSpinner } from 'src/common/components/loading-spinner';
import { Pagination } from 'src/common/Pagination';
import { DataTable } from 'src/common/components/table/data-table';
import { ChatbotEvent, ChatbotMessage } from 'src/editor/insights/constants';
import { ImageRenderer } from 'src/common/ImageRenderer';
import { getJourneyAliasById } from '../../helpers/get-journey-alias-by-id';
import { EllipsizedText } from 'src/common/components/table/ellipsized-text';
import { TimePeriodParam } from '../../constants';
import { TableHeaderWithTooltip } from '../../table-header-with-tooltip';
import { useChatbotEvents } from '../use-chatbot-events';
import { TalkToJourneyMessage } from 'src/editor/content-creation/talk-to-journey/types';
import { functionNoop } from 'src/utils/function/noop';
import { TalkToJourneyPanel } from 'src/editor/content-creation/talk-to-journey/panel';

type Props = {
  journey: Journey;
  timePeriodParam?: TimePeriodParam;
  setAllChatbotSessionsFetcher: (fetcher: () => Promise<{ events: any[] }>) => void;
};

export const DashboardInsightsJourneysChatbotSessionsTable = ({ journey, setAllChatbotSessionsFetcher }: Props) => {
  const {
    filterBarParams: { search_term, page, filters },
    updatePage,
  } = useFilterBarParams();

  const [readOnlyMessages, setReadOnlyMessages] = useState<TalkToJourneyMessage[]>([]);

  const openRef = useRef(functionNoop);
  const closeRef = useRef(functionNoop);

  // We use useMemo here so that we pass the same object to the api hook below.
  const insightsFilters = useMemo(
    () => ({
      email: search_term,
      node_ids: filters?.step?.map((nodeId) => parseInt(nodeId)),
      alias_ids: filters?.link?.map((aliasId) => parseInt(aliasId)),
      event_types: ['chatbot_initialize', 'chatbot_message_exchange'],
      all: undefined,
    }),
    [search_term, filters]
  );

  const { fetching, pages, showPagination, chatbotEvents } = useChatbotEvents({
    journeyUuid: journey.uuid,
    page,
    filters: insightsFilters,
  });

  const allInsightsFilters = useMemo(
    () => ({
      email: search_term,
      node_ids: filters?.step?.map((nodeId) => parseInt(nodeId)),
      alias_ids: filters?.link?.map((aliasId) => parseInt(aliasId)),
      event_types: ['chatbot_initialize', 'chatbot_message_exchange'],
      all: true,
    }),
    [search_term, filters]
  );

  const { fetchJourneyChatbotInsights } = useChatbotEvents({
    journeyUuid: journey.uuid,
    page,
    filters: allInsightsFilters,
    preventImmediateFetching: true,
  });

  useEffect(() => {
    setAllChatbotSessionsFetcher(fetchJourneyChatbotInsights);
  });

  const columns = useMemo(
    () => [
      generateColumn({
        Header: <TableHeaderWithTooltip tooltip='Visitor name or email'>Name</TableHeaderWithTooltip>,
        accessor: 'name',
        width: 176,
        Cell: ({ row }: { row: any; index: number }) => <EllipsizedText text={row.original.name || 'Anonymous'} />,
      }),
      generateColumn({
        Header: <TableHeaderWithTooltip tooltip='When they viewed'>Viewed</TableHeaderWithTooltip>,
        accessor: 'viewed',
        width: 160,
        Cell: ({ row }: { row: any; index: number }) => row.original.created_at,
      }),
      generateColumn({
        Header: <TableHeaderWithTooltip tooltip='Visitor email if captured'>Email address</TableHeaderWithTooltip>,
        accessor: 'email_address',
        width: 160,
        Cell: ({ row }: { row: any; index: number }) => <EllipsizedText text={row.original.email || ''} />,
      }),
      generateColumn({
        Header: <TableHeaderWithTooltip tooltip='Visitor email if captured'>Chat</TableHeaderWithTooltip>,
        accessor: 'events',
        width: 240,
        Cell: ({ row }: { row: any; index: number }) => {
          const chatbotEvent = row.original as ChatbotEvent;
          const messages = chatbotEvent.messages as ChatbotMessage[];

          let message = '';
          if (messages) {
            if (messages.length > 1) {
              message = messages[1].question;
            } else {
              message = messages[0].answer;
            }
          }
          return <EllipsizedText text={message || ''} />;
        },
      }),
      generateColumn({
        Header: <TableHeaderWithTooltip tooltip='Inferred company name'>Company</TableHeaderWithTooltip>,
        accessor: 'company',
        width: 138,
        Cell: ({ row }: { row: any; index: number }) => {
          const visitorView = row.original as ChatbotEvent;
          const { company } = visitorView;
          if (company) {
            return (
              <div className='flex items-start gap-2'>
                {company.logo && <ImageRenderer className='rounded-md w-5 h-5' src={company.logo} />}
                {company.name && <div>{company.name}</div>}
              </div>
            );
          }
          return null;
        },
      }),
      generateColumn({
        Header: <TableHeaderWithTooltip tooltip='Location of viewer'>Location</TableHeaderWithTooltip>,
        accessor: 'location',
        width: 138,
        Cell: ({ row }: { row: any; index: number }) => row.original.location || '',
      }),
      generateColumn({
        Header: <TableHeaderWithTooltip tooltip='IP of viewer'>IP address</TableHeaderWithTooltip>,
        accessor: 'ip_address',
        width: 138,
        Cell: ({ row }: { row: any; index: number }) => row.original.ip_address || '',
      }),
      generateColumn({
        Header: <TableHeaderWithTooltip tooltip='Link name that was opened'>Journey</TableHeaderWithTooltip>,
        accessor: 'journey',
        width: 208,
        Cell: ({ row }: { row: any; index: number }) => {
          const visitorView = row.original as ChatbotEvent;
          const { alias_id } = visitorView;
          if (alias_id) {
            return (
              <div>
                <EllipsizedText text={journey.name || ''} />
              </div>
            );
          }
          return null;
        },
      }),
    ],
    [journey, getJourneyAliasById]
  );

  const onPageChange = useCallback(
    (e: any, newPage: number) => {
      updatePage(newPage);
    },
    [updatePage]
  );

  const onRowClicked = (row: Row) => {
    const chatbotEvent = row.original as ChatbotEvent;
    const messages = chatbotEvent.messages as ChatbotMessage[];
    openRef?.current?.();
    // loop through all messages inside the event and create a list of TalkToJourneyMessage array

    let talkToJourneyMessages: TalkToJourneyMessage[] = [];

    messages.forEach((message: ChatbotMessage) => {
      const eventType = message.event_type;

      if (eventType === 'chatbot_initialize') {
        // this is the first greeting message from the bot
        talkToJourneyMessages.push({
          role: 'assistant',
          text: message.answer,
        } as TalkToJourneyMessage);
      } else {
        // this is the actualy question / answer exchange, so we need to insert 2 items to the list
        if (message.question) {
          talkToJourneyMessages.push({
            role: 'user',
            text: message.question,
          } as TalkToJourneyMessage);
        }

        if (message.answer) {
          talkToJourneyMessages.push({
            role: 'assistant',
            text: message.answer,
          } as TalkToJourneyMessage);
        }
      }
    });

    setReadOnlyMessages(talkToJourneyMessages);
  };

  return (
    <div className='mb-6'>
      {fetching ? (
        <FixedLoadingSpinner />
      ) : chatbotEvents.length === 0 ? (
        <div className='text-bedrock-p'>No chat activity found yet.</div>
      ) : (
        <DataTable tableClassName='w-full' columns={columns} data={chatbotEvents} onRowClick={onRowClicked} />
      )}
      {showPagination && pages > 1 && (
        <div className='mt-4'>
          <Pagination count={pages} page={page} onChange={onPageChange} />
        </div>
      )}
      <TalkToJourneyPanel
        layoutMode={'web'}
        journeyUUID={journey.uuid}
        openRef={openRef}
        closeRef={closeRef}
        onSourceClick={functionNoop}
        findBlockByContentUuid={(contentUuid: string) => {
          return null;
        }}
        findSectionById={(sectionId: string) => {
          return null;
        }}
        inEditor={true}
        isReadOnly={true}
        readOnlyMessages={readOnlyMessages}
        onClose={() => {
          // reset all local state
          setReadOnlyMessages([]);
        }}
      />
    </div>
  );
};
